import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Search, Loader2, Users, Smartphone, Mail, ShieldAlert,
  Wrench, RefreshCw, CheckCircle2, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso || '—'; }
}

function PlatformBadge({ platform }) {
  const colors = {
    ios: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    android: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    both: 'bg-[#9333EA]/20 text-[#c4a3ff] border-[#9333EA]/30',
  };
  const cls = colors[platform] || 'bg-white/10 text-[#A19DB0] border-white/20';
  return (
    <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${cls}`}>
      {platform || 'unknown'}
    </span>
  );
}

export default function AdminWaitlistPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');

  const fetchWaitlist = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/waitlist`);
      setEntries(res.data.entries || []);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      setError(err.response?.data?.detail || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchWaitlist();
  }, [fetchWaitlist]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      const matchesQuery = !query || e.email.toLowerCase().includes(query.toLowerCase());
      const matchesPlatform = platformFilter === 'all' || e.platform === platformFilter;
      return matchesQuery && matchesPlatform;
    });
  }, [entries, query, platformFilter]);

  const stats = useMemo(() => ({
    total: entries.length,
    ios: entries.filter(e => e.platform === 'ios').length,
    android: entries.filter(e => e.platform === 'android').length,
    both: entries.filter(e => e.platform === 'both').length,
  }), [entries]);

  const handleExportCSV = () => {
    if (filtered.length === 0) {
      toast.error('No entries to export');
      return;
    }
    const header = ['email', 'platform', 'source', 'created_at'];
    const escape = (v) => {
      const s = String(v ?? '');
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = [
      header.join(','),
      ...filtered.map(e => header.map(h => escape(e[h])).join(',')),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.setAttribute('download', `futurevault-waitlist-${date}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} ${filtered.length === 1 ? 'entry' : 'entries'}`);
  };

  const handleCopyEmails = async () => {
    if (filtered.length === 0) {
      toast.error('No emails to copy');
      return;
    }
    const emails = filtered.map(e => e.email).join(', ');
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`Copied ${filtered.length} ${filtered.length === 1 ? 'email' : 'emails'} to clipboard`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // Access control UI (client side — server already enforces)
  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-vault flex items-center justify-center px-4">
        <div className="max-w-md w-full p-8 bg-[#0A0A0F] rounded-xl border border-white/10 text-center" data-testid="admin-access-denied">
          <ShieldAlert className="h-10 w-10 text-[#9333EA] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-white mb-2">Admin Access Required</h1>
          <p className="text-[#A19DB0] mb-6">You don't have permission to view this page.</p>
          <Link to="/dashboard">
            <Button className="btn-primary">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vault" data-testid="admin-waitlist-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-[#A19DB0] hover:text-white hover:bg-white/5" data-testid="admin-back-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
              </Link>
              <span className="font-heading text-lg font-bold text-white tracking-tight">Waitlist</span>
            </div>
            <Button
              onClick={handleExportCSV}
              disabled={loading || filtered.length === 0}
              className="btn-primary h-9 text-sm"
              data-testid="admin-export-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={<Users className="h-4 w-4 text-[#9333EA]" />} label="Total signups" value={stats.total} testid="stat-total" />
          <StatCard icon={<Smartphone className="h-4 w-4 text-blue-400" />} label="iOS" value={stats.ios} testid="stat-ios" />
          <StatCard icon={<Smartphone className="h-4 w-4 text-emerald-400" />} label="Android" value={stats.android} testid="stat-android" />
          <StatCard icon={<Smartphone className="h-4 w-4 text-[#c4a3ff]" />} label="Both" value={stats.both} testid="stat-both" />
        </div>

        {/* Admin payment tools */}
        <PaymentRecoveryPanel />

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A19DB0]" />
            <Input
              type="text"
              placeholder="Search by email..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 bg-[#0A0A0F] border-white/10 text-white placeholder:text-[#6b6880] h-10"
              data-testid="admin-waitlist-search"
            />
          </div>
          <div className="flex gap-1 bg-[#0A0A0F] border border-white/10 rounded-md p-1">
            {['all', 'ios', 'android', 'both'].map((p) => (
              <button
                key={p}
                onClick={() => setPlatformFilter(p)}
                className={`px-3 py-1 text-xs font-semibold uppercase rounded transition-colors ${
                  platformFilter === p
                    ? 'bg-[#9333EA] text-white'
                    : 'text-[#A19DB0] hover:text-white'
                }`}
                data-testid={`admin-filter-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
          <Button
            onClick={handleCopyEmails}
            disabled={loading || filtered.length === 0}
            variant="outline"
            className="btn-secondary h-10"
            data-testid="admin-copy-emails-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            Copy emails
          </Button>
        </div>

        {/* Table */}
        <div className="bg-[#0A0A0F] rounded-xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="h-6 w-6 text-[#9333EA] animate-spin" />
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <p className="text-red-400 mb-4" data-testid="admin-error">{error}</p>
              <Button onClick={fetchWaitlist} className="btn-primary" data-testid="admin-retry-btn">Retry</Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center" data-testid="admin-empty-state">
              <Users className="h-10 w-10 text-[#A19DB0] mx-auto mb-3 opacity-50" />
              <p className="text-[#A19DB0]">
                {entries.length === 0
                  ? "No waitlist signups yet. Share your landing page to start collecting emails."
                  : "No entries match your filters."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-waitlist-table">
                <thead>
                  <tr className="border-b border-white/10 text-left text-[10px] font-semibold uppercase tracking-wider text-[#A19DB0]">
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Platform</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3 text-right">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry, i) => (
                    <tr
                      key={`${entry.email}-${i}`}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      data-testid={`waitlist-row-${i}`}
                    >
                      <td className="px-4 py-3 text-white font-medium break-all">{entry.email}</td>
                      <td className="px-4 py-3"><PlatformBadge platform={entry.platform} /></td>
                      <td className="px-4 py-3 text-[#A19DB0]">{entry.source || '—'}</td>
                      <td className="px-4 py-3 text-[#A19DB0] text-right whitespace-nowrap">{formatDate(entry.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && filtered.length > 0 && (
          <p className="text-xs text-[#A19DB0] mt-3 text-center">
            Showing {filtered.length} of {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          </p>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="p-4 bg-[#0A0A0F] rounded-xl border border-white/10" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#A19DB0]">{label}</span>
      </div>
      <div className="font-mono text-2xl font-bold text-white">{value}</div>
    </div>
  );
}


function PaymentRecoveryPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handleRecover = async () => {
    if (!window.confirm('Re-verify every pending payment against Stripe and credit any that were paid. This is safe to run anytime.')) {
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/recover-pending-payments`);
      setResult(res.data);
      const creditedCount = (res.data.processed || []).filter(p => p.result?.credited).length;
      const expiredCount = (res.data.processed || []).filter(p => p.result?.expired).length;
      const abandonedCount = (res.data.processed || []).filter(p => p.result?.marked_abandoned).length;
      if (creditedCount > 0) {
        toast.success(`Credited ${creditedCount} stuck payment${creditedCount === 1 ? '' : 's'} to vaults`);
      } else {
        toast.success(`Scan complete. ${expiredCount} expired, ${abandonedCount} abandoned, 0 needed crediting.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recovery failed');
    } finally {
      setRunning(false);
    }
  };

  const summary = result ? {
    total: result.total_pending_found ?? 0,
    credited: (result.processed || []).filter(p => p.result?.credited).length,
    expired: (result.processed || []).filter(p => p.result?.expired).length,
    abandoned: (result.processed || []).filter(p => p.result?.marked_abandoned).length,
    stillPending: (result.processed || []).filter(p => p.result?.still_pending).length,
    errors: (result.errors || []).length,
  } : null;

  return (
    <div className="mb-6 p-5 bg-[#0A0A0F] rounded-xl border border-white/10" data-testid="payment-recovery-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-[#9333EA]" />
          <h2 className="font-heading text-sm font-bold text-white uppercase tracking-wider">Payment Tools</h2>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <p className="text-white text-sm font-semibold mb-1">Recover stuck payments</p>
          <p className="text-[#A19DB0] text-xs">
            Scans all <code className="text-[#c4a3ff]">pending</code> transactions, re-verifies them against Stripe, and credits any that were actually paid. Safe to run anytime.
          </p>
        </div>
        <Button
          onClick={handleRecover}
          disabled={running}
          className="btn-primary h-10 text-sm whitespace-nowrap"
          data-testid="recover-payments-btn"
        >
          {running ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning...</>
          ) : (
            <><RefreshCw className="h-4 w-4 mr-2" /> Run recovery</>
          )}
        </Button>
      </div>

      {summary && (
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="recovery-result">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            <RecoveryStat label="Found" value={summary.total} color="text-white" />
            <RecoveryStat label="Credited" value={summary.credited} color="text-emerald-400" highlight={summary.credited > 0} />
            <RecoveryStat label="Expired" value={summary.expired} color="text-[#A19DB0]" />
            <RecoveryStat label="Abandoned" value={summary.abandoned} color="text-[#A19DB0]" />
            <RecoveryStat label="Errors" value={summary.errors} color={summary.errors > 0 ? 'text-red-400' : 'text-[#A19DB0]'} />
          </div>

          {summary.credited > 0 && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-start gap-2" data-testid="recovery-success">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
              <p className="text-xs text-emerald-300">
                Successfully credited <span className="font-bold">{summary.credited}</span> {summary.credited === 1 ? 'payment' : 'payments'} to user vaults. Affected users will see the correct balance on their next dashboard load.
              </p>
            </div>
          )}

          {summary.errors > 0 && (
            <details className="mt-2 p-3 bg-red-500/5 border border-red-500/20 rounded-lg" data-testid="recovery-errors">
              <summary className="cursor-pointer text-xs text-red-300 font-semibold flex items-center gap-2">
                <AlertCircle className="h-3.5 w-3.5" />
                {summary.errors} {summary.errors === 1 ? 'error' : 'errors'}
              </summary>
              <div className="mt-2 space-y-1 font-mono text-[10px] text-[#A19DB0] max-h-40 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="break-all">
                    <span className="text-red-300">✗</span> {e.session_id?.slice(0, 24)}...: {e.error}
                  </div>
                ))}
              </div>
            </details>
          )}

          {summary.total === 0 && (
            <p className="text-xs text-[#A19DB0] text-center">No pending payments found. All good.</p>
          )}
        </div>
      )}
    </div>
  );
}

function RecoveryStat({ label, value, color = 'text-white', highlight = false }) {
  return (
    <div className={`p-2 rounded-lg text-center ${highlight ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-[#12121A] border border-white/5'}`}>
      <div className={`font-mono text-lg font-bold ${color}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-[#A19DB0]">{label}</div>
    </div>
  );
}
